/**
 * Burimi i kandidatit — një fjalor, jo tekst i lirë.
 *
 * Burimi shkruhej si varg: «LinkedIn», «linkedin», «Linkedin », «LI». Për HR-në
 * ishin i njëjti kanal; për raportin ishin katër rreshta, dhe pyetja «nga na
 * vijnë kandidatët më të mirë» mbetej pa përgjigje. Këtu kodet janë të fiksuara
 * dhe çdo variant i vjetër normalizohet — pa e prekur atë që tashmë është ruajtur
 * te baza, sepse migrimi i tekstit të lirë fshin edhe atë që s'e kuptojmë.
 *
 * `public` shkruhet nga vetë sistemi kur aplikimi vjen nga faqja e konkursit,
 * ndaj nuk zgjidhet me dorë.
 */

export const RECRUITMENT_SOURCES = [
  { code: 'public',     label: 'Faqja publike', system: true },
  { code: 'linkedin',   label: 'LinkedIn' },
  { code: 'email',      label: 'Email' },
  { code: 'website',    label: 'Faqja e kompanisë' },
  { code: 'referral',   label: 'Referim' },
  { code: 'agency',     label: 'Agjenci rekrutimi' },
  { code: 'job_fair',   label: 'Panair pune' },
  { code: 'walk_in',    label: 'Erdhi vetë' },
  { code: 'internal',   label: 'Kandidat i brendshëm' },
  { code: 'other',      label: 'Tjetër' },
];

/** Variantet e vjetra dhe shkrimet e ndryshme → kodi kanonik. */
const ALIASES = {
  li: 'linkedin', 'linked in': 'linkedin',
  'faqja publike': 'public', 'public site': 'public', portal: 'public',
  web: 'website', 'faqja e kompanise': 'website', 'faqja e kompanisë': 'website',
  referim: 'referral', rekomandim: 'referral', reference: 'referral',
  agjenci: 'agency', 'agjenci rekrutimi': 'agency',
  'panair pune': 'job_fair', panair: 'job_fair', fair: 'job_fair',
  'erdhi vete': 'walk_in', 'erdhi vetë': 'walk_in', walkin: 'walk_in',
  'i brendshem': 'internal', 'i brendshëm': 'internal',
  tjeter: 'other', 'tjetër': 'other',
  mail: 'email', 'e-mail': 'email',
};

const CODES = new Set(RECRUITMENT_SOURCES.map((s) => s.code));

// Etiketa e shfaqur është vetë një variant i pranueshëm: ajo që dikur u ruajt
// si tekst i lirë ishte pikërisht ajo që lexohej në ekran.
const BY_LABEL = Object.fromEntries(
  RECRUITMENT_SOURCES.map((s) => [s.label.toLowerCase(), s.code]));

/**
 * Kodi kanonik i një burimi të shkruar në çfarëdo forme.
 * Një vlerë që nuk njihet kthehet si `other`, jo si vetvetja: raporti me dhjetë
 * rreshta unikë është pikërisht ajo që po zgjidhet këtu.
 */
export function normalizeSource(raw) {
  const v = String(raw ?? '').trim().toLowerCase().replace(/[\s_-]+/g, ' ');
  if (!v) return '';
  const flat = v.replace(/ /g, '_');
  if (CODES.has(flat)) return flat;
  if (ALIASES[v]) return ALIASES[v];
  if (BY_LABEL[v]) return BY_LABEL[v];
  const nospace = v.replace(/ /g, '');
  if (CODES.has(nospace)) return nospace;
  if (ALIASES[nospace]) return ALIASES[nospace];
  return 'other';
}

/** Etiketa për shfaqje. Një burim i paruajtur mbetet vizë, jo «Tjetër». */
export function sourceLabel(raw) {
  const code = normalizeSource(raw);
  if (!code) return '—';
  return (RECRUITMENT_SOURCES.find((s) => s.code === code) || {}).label || 'Tjetër';
}

/** Burimet që HR-ja mund t'i zgjedhë me dorë (pa ato që i shkruan sistemi). */
export const SELECTABLE_SOURCES = RECRUITMENT_SOURCES.filter((s) => !s.system);
